import { createNavigation } from '../components/navigation.js';
import { api } from '../lib/api.js';
import { getLanguage, setLanguage, t } from '../lib/i18n.js';
import { getCurrentUser } from '../lib/keycloak.js';

export async function renderSettings() {
  const app = document.getElementById('app');
  const currentLang = getLanguage();
  const currentUser = await getCurrentUser();
  const canManageAccess = currentUser?.role === 'admin';

  app.innerHTML = '';
  app.appendChild(await createNavigation());

  const main = document.createElement('main');
  main.innerHTML = `
    <div class="page-container">
      <div class="page-header">
        <h1>${t('settings_title')}</h1>
      </div>
      
      <div class="settings-container">
        <div class="settings-section">
          <h2>${t('settings_language')}</h2>
          <div class="language-selector">
            <button 
              class="language-btn ${currentLang === 'fo' ? 'active' : ''}"
              data-lang="fo"
            >
              🇫🇴 ${t('settings_language_fo')}
            </button>
            <button 
              class="language-btn ${currentLang === 'en' ? 'active' : ''}"
              data-lang="en"
            >
              🇬🇧 ${t('settings_language_en')}
            </button>
          </div>
        </div>

        ${
          canManageAccess
            ? `
              <div class="settings-section">
                <div class="settings-section-header">
                  <div>
                    <h2>${t('settings_access_title')}</h2>
                    <p>${t('settings_access_subtitle')}</p>
                  </div>
                </div>
                <div id="access-users-content" class="settings-loading">
                  <div class="spinner"></div>
                </div>
              </div>
            `
            : ''
        }
      </div>
    </div>
  `;

  app.appendChild(main);
  initSettings();

  if (canManageAccess) {
    await loadAccessUsers();
  }
}

function initSettings() {
  // Handle language selection
  document.querySelectorAll('.language-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const lang = e.currentTarget.dataset.lang;
      setLanguage(lang);

      // Show success message
      showToast(t('settings_saved'));

      // Refresh page to apply new language
      setTimeout(() => {
        window.location.reload();
      }, 500);
    });
  });
}

async function loadAccessUsers() {
  const container = document.getElementById('access-users-content');
  if (!container) return;

  try {
    const { users = [], roles = [] } = await api.getUsers();

    if (!users.length) {
      container.innerHTML = `
        <div class="settings-empty">
          ${t('settings_access_no_users')}
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <div class="table-container">
        <table class="table settings-users-table">
          <thead>
            <tr>
              <th>${t('settings_access_user')}</th>
              <th>${t('settings_access_role')}</th>
              <th>${t('settings_access_updated')}</th>
            </tr>
          </thead>
          <tbody>
            ${users.map((user) => renderUserRow(user, roles)).join('')}
          </tbody>
        </table>
      </div>
    `;

    container.querySelectorAll('[data-role-select]').forEach((select) => {
      select.addEventListener('change', async (event) => {
        await handleRoleChange(event.currentTarget);
      });
    });
  } catch (error) {
    console.error('Failed to load users:', error);
    container.innerHTML = `
      <div class="settings-empty settings-empty-error">
        ${t('settings_access_load_error')}
      </div>
    `;
  }
}

function renderUserRow(user, roles) {
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const roleOptions = roles
    .map(
      (role) => `
        <option value="${escapeHtml(role)}" ${role === user.role ? 'selected' : ''}>
          ${escapeHtml(getRoleLabel(role))}
        </option>
      `,
    )
    .join('');

  return `
    <tr>
      <td>
        <strong>${escapeHtml(displayName || user.email || user.id)}</strong>
        <div class="settings-user-email">${escapeHtml(user.email || user.id)}</div>
      </td>
      <td>
        <select
          class="form-select settings-role-select"
          data-role-select
          data-user-id="${escapeHtml(user.id)}"
          data-current-role="${escapeHtml(user.role)}"
          aria-label="${escapeHtml(t('settings_access_role'))}"
        >
          ${roleOptions}
        </select>
      </td>
      <td>${formatDate(user.updatedAt)}</td>
    </tr>
  `;
}

async function handleRoleChange(select) {
  const userId = select.dataset.userId;
  const previousRole = select.dataset.currentRole;
  const nextRole = select.value;

  select.disabled = true;

  try {
    const { user } = await api.updateUserRole(userId, nextRole);
    select.dataset.currentRole = user.role;
    select.value = user.role;
    showToast(t('settings_access_saved'));
  } catch (error) {
    console.error('Failed to update user role:', error);
    select.value = previousRole;
    showToast(error.message || t('settings_access_save_error'), 'error');
  } finally {
    select.disabled = false;
  }
}

function getRoleLabel(role) {
  const labels = {
    admin: t('settings_role_admin'),
    filmmaker: t('settings_role_filmmaker'),
    user: t('settings_role_user'),
  };

  return labels[role] || role;
}

function formatDate(value) {
  const timestamp = Number(value || 0);
  if (!timestamp) return '-';

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp));
}

function showToast(message, type = 'success') {
  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.classList.add('show');
  }, 10);

  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return entities[char];
  });
}
